/**
 * Helpers for the extension's Car Details page.
 * Ported from the main BidCars frontend (Car.jsx) so the extension matches the
 * marketplace 1:1 — icons, damage/title/condition translations, formatters.
 *
 * The extension renders in English only; we drop the runtime locale dependency.
 */

// ─── Inline spec icons (Lucide-style 18×18) ────────────────────────────────
import React from "react";

export const specIcons = {
  car_odometer: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  ),
  car_has_key: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  ),
  car_engine: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 10h10M7 14h10M12 6v4M12 14v4M5 10V8a2 2 0 012-2h10a2 2 0 012 2v2M5 14v2a2 2 0 002 2h10a2 2 0 002-2v-2" />
    </svg>
  ),
  car_drivetrain: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v6m0 6v6M3 12h6m6 0h6" />
    </svg>
  ),
  car_transmission: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  car_fuel: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 22V5a2 2 0 012-2h8a2 2 0 012 2v17M3 22h12M13 10h2a2 2 0 012 2v3M18 22V8l3-3" />
      <path d="M7 10h4" />
    </svg>
  ),
  car_cylinders: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v4M12 18v4" />
    </svg>
  ),
  car_body: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M5 17a2 2 0 01-2-2V9l3-5h12l3 5v6a2 2 0 01-2 2M5 17v2h2v-2m10 0v2h2v-2" />
      <circle cx="7.5" cy="14.5" r="1.5" />
      <circle cx="16.5" cy="14.5" r="1.5" />
    </svg>
  ),
  car_color: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="13.5" cy="6.5" r="2.5" />
      <circle cx="17.5" cy="10.5" r="2.5" />
      <circle cx="8.5" cy="7.5" r="2.5" />
      <circle cx="6.5" cy="12.5" r="2.5" />
      <path d="M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12a10 10 0 005 8.66" />
    </svg>
  ),
  car_trim: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  ),
  car_vin: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M7 15V9M10 15V9l3 6V9M17 9h-3v6h3M14 12h2" />
    </svg>
  ),
  car_lot_number: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  ),
  car_date_added: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4" />
    </svg>
  ),
  car_auction_date: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <circle cx="16" cy="16" r="2" />
      <path d="M16 14v2" />
    </svg>
  ),
  car_sale_name: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      <path d="M9 22V12h6v10" />
    </svg>
  ),
  car_location: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  ),
  car_seller: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  car_seller_type: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  ),
  car_primary_damage: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  car_secondary_damage: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  car_condition: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  car_document: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
    </svg>
  ),
  car_retail_value: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
};

// ─── Formatters ─────────────────────────────────────────────────────────────
export function formatMoney(n) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);
}

export function formatDate(d) {
  if (!d) return null;
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

export function formatSaleTime(raw) {
  const str = String(raw ?? "").trim();
  if (!str) return "";
  const digits = str.replace(/\D/g, "").padStart(4, "0");
  const hour = parseInt(digits.slice(0, 2), 10);
  const minute = digits.slice(2, 4);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute} ${ampm}`;
}

export function formatOdometer(miles, km, brand) {
  let base = "";
  if (typeof miles === "number" && Number.isFinite(miles)) base = `${miles.toLocaleString()} mi`;
  else if (typeof km === "number" && Number.isFinite(km)) base = `${km.toLocaleString()} km`;
  if (!base) return null;
  if (!brand) return base;
  const b = String(brand).toUpperCase();
  const isActual = b === "ACTUAL";
  const isBad = b.includes("NOT ACTUAL") || b.includes("EXCEEDS");
  if (isActual || isBad) {
    const color = isActual ? "#16a34a" : "#dc2626";
    const icon = isActual ? "✅" : "⚠️";
    return (
      <>
        {base}{" "}
        <span style={{ color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
          <span style={{ fontSize: "0.85em" }}>{icon}</span>
          {brand}
        </span>
      </>
    );
  }
  return `${base} (${brand})`;
}

export function formatKeyStatus(hasKeys) {
  if (hasKeys == null) return null;
  const color = hasKeys ? "#16a34a" : "#dc2626";
  const icon = hasKeys ? "✅" : "❌";
  const label = hasKeys ? "Yes" : "No";
  return (
    <span style={{ color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ fontSize: "0.85em" }}>{icon}</span>
      {label}
    </span>
  );
}

export function maskVin(vin) {
  const raw = String(vin ?? "").trim();
  if (!raw) return null;
  return <span style={{ fontFamily: "monospace" }}>{raw}</span>;
}

// ─── Damage translation (short labels for common Copart values) ─────────────
const DAMAGE_LABELS = {
  "FRONT END": "Front End",
  "REAR END": "Rear End",
  "SIDE": "Side",
  "HAIL": "Hail",
  "WATER/FLOOD": "Water/Flood",
  "FLOOD": "Flood",
  "MECHANICAL": "Mechanical",
  "NONE": "None",
  "VANDALISM": "Vandalism",
  "MINOR DENT/SCRATCHES": "Minor Dent/Scratches",
  "NORMAL WEAR": "Normal Wear",
  "ROLLOVER": "Rollover",
  "ALL OVER": "All Over",
  "BURN": "Burn",
  "UNDERCARRIAGE": "Undercarriage",
  "TOP/ROOF": "Top/Roof",
  "GLASS": "Glass",
  "THEFT": "Theft",
};
export function translateDamage(val) {
  if (!val) return null;
  const upper = String(val).toUpperCase();
  return DAMAGE_LABELS[upper] || val;
}

// ─── Title-type colouring (salvage vs clean) ────────────────────────────────
// Only "CD" is universally Certificate of Destruction; other C-prefix codes
// (CZ, CB, CF, CY, CR, etc.) are state-specific Clean Title variations, not
// destruction. "DV" = Dealer Vehicle, not dismantled.
const RED_TITLE_PREFIXES = new Set([
  "BS","JT","JC","JK","JR","JB","PS","PO","PX","PL","PT","PV","PN","PP","PF","PC","PR","PA",
  "CD",
  "NR","NX","NU","NB","NF","NQ","NP","NL","NS","NT",
  "DA","DC","DL","DQ","DT","DU","DY","DN","DP","DR","DD","DE","DS","DZ","DM","DI","DJ","D2","D","DO",
]);
const GREEN_TITLE_CODES = new Set(["CT", "MS", "CZ"]);

const TITLE_TYPE_LABELS = {
  CT: "Clean Title",
  MS: "Manufacturer's Statement",
  ST: "Salvage Title",
  SC: "Salvage Certificate",
  SV: "Salvage",
  RB: "Rebuildable",
  RS: "Rebuilt",
  RT: "Rebuilt",
  RC: "Rebuilt Certificate",
  BS: "Bill of Sale",
  BP: "Bonded Title",
  NR: "Non-Repairable",
  NT: "No Title",
  CD: "Certificate of Destruction",
  CZ: "Dealer Only Clean Title",
  DV: "Dealer Vehicle",
  JT: "Junk Title",
  PS: "Parts Only",
  FS: "Flood/Salvage",
  LS: "Lien Sale",
  EX: "Export",
  ET: "Export Title",
};

export function translateTitleType(titleType, saleTitleState) {
  if (!titleType) return null;
  const code = String(titleType).toUpperCase();
  const friendly = TITLE_TYPE_LABELS[code] || titleType;
  const text = saleTitleState ? `${friendly} (${saleTitleState})` : friendly;
  let color = null;
  let icon = null;
  if (GREEN_TITLE_CODES.has(code)) { color = "#16a34a"; icon = "✅"; }
  else if (RED_TITLE_PREFIXES.has(code)) { color = "#dc2626"; icon = "⛔"; }
  if (!color) return text;
  return (
    <span style={{ color, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ fontSize: "0.85em" }}>{icon}</span>
      {text}
    </span>
  );
}

// ─── Run-status / condition (trimmed mapping for common codes) ──────────────
const CONDITION_MAP = {
  RUN_AND_DRIVE: { label: "Run & Drive", dot: "#16a34a" },
  STARTS:        { label: "Starts",       dot: "#f59e0b" },
  ENHANCED:      { label: "Enhanced",     dot: "#22c55e" },
  NOT_STARTS:    { label: "Does not start", dot: "#dc2626" },
  IS_INOP:       { label: "Inoperable",   dot: "#dc2626" },
  NO_VERIFICATION: { label: "No verification", dot: "#64748b" },
};

export function translateCondition(lotCondition, runStatus) {
  const rs = runStatus && runStatus !== "DEFAULT" ? String(runStatus).toUpperCase() : null;
  const lc = lotCondition && lotCondition !== "DEFAULT" ? String(lotCondition).toUpperCase() : null;
  const match =
    (rs && CONDITION_MAP[rs]) ||
    (lc && CONDITION_MAP[lc]) ||
    null;
  if (!match) {
    const raw = rs || lc;
    return raw
      ? <span style={{ fontWeight: 600 }}>{toTitleCase(raw)}</span>
      : null;
  }
  return (
    <span style={{ color: match.dot, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: match.dot, flexShrink: 0 }} />
      {match.label}
    </span>
  );
}

function toTitleCase(s) {
  return String(s)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Transmission / Fuel translations ───────────────────────────────────────
const TRANSMISSION_LABELS = {
  Automatic: "Automatic",
  Manual: "Manual",
  CVT: "CVT",
};
export function translateTransmission(val) {
  if (!val) return null;
  return TRANSMISSION_LABELS[val] || val;
}

const FUEL_LABELS = {
  Gasoline: "Gasoline",
  Gas: "Gasoline",
  Diesel: "Diesel",
  Electric: "Electric",
  Hybrid: "Hybrid",
  "Plug-in Hybrid": "Plug-in Hybrid",
  "Flex Fuel": "Flex Fuel",
  "Natural Gas": "Natural Gas",
};
export function translateFuel(val) {
  if (!val) return null;
  return FUEL_LABELS[val] || val;
}

// ─── Sale-status / seller-type helpers ──────────────────────────────────────
const SALE_STATUS_LABELS = {
  "On Approval": "On Approval",
  "On Minimum Bid": "On Minimum Bid",
  "Pure Sale": "Pure Sale",
};
export function translateSaleStatus(val) {
  if (!val) return null;
  for (const key of Object.keys(SALE_STATUS_LABELS)) {
    if (String(val).includes(key)) return SALE_STATUS_LABELS[key];
  }
  return val;
}

// ─── Image URL resolver (matches main frontend behaviour) ──────────────────
const NO_PREVIEW_SOURCE = "no-preview";
const API_BASE = (typeof window !== "undefined" && window.COPART_API_BASE) || "http://localhost:4000";

export function resolveImageUrl(img) {
  if (!img) return "";
  if (img.sourceUrl === NO_PREVIEW_SOURCE) return "";
  let url = img.localPath || img.sourceUrl || (typeof img === "string" ? img : "");
  if (!url) return "";
  if (!/^https?:\/\//i.test(url)) {
    url = API_BASE.replace(/\/+$/, "") + (url.startsWith("/") ? "" : "/") + url;
  }
  return url;
}

export function toCopartHq(url) {
  const s = String(url || "").trim();
  if (!s) return "";
  if (/_hrs\./i.test(s) || /_ful\./i.test(s)) return s;
  return s.replace(/_thb\.jpg/i, "_hrs.jpg").replace(/_thb\./i, "_hrs.");
}

export function toCopartThumb(url) {
  const s = String(url || "").trim();
  if (!s) return "";
  if (/_thb\./i.test(s)) return s;
  return s
    .replace(/_hrs\.jpg/i, "_thb.jpg")
    .replace(/_hrs\./i, "_thb.")
    .replace(/_ful\.jpg/i, "_thb.jpg")
    .replace(/_ful\./i, "_thb.");
}

// ─── Countdown hook — computes d/h/m/s remaining from a Unix timestamp ─────
import { useEffect, useState } from "react";

export function useCountdown(endTimestamp) {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    if (endTimestamp == null) return undefined;
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, [endTimestamp]);

  if (endTimestamp == null || !Number.isFinite(endTimestamp)) return null;
  const diff = Math.max(0, endTimestamp - now);
  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return { days, hours, minutes, seconds, expired: diff === 0 };
}

// ─── Skeleton placeholder — shown in place of missing values ───────────────
export function Skel({ w = 90 }) {
  return <span className="skel skel--line" style={{ width: w }} aria-hidden="true" />;
}

// Returns value if it's a meaningful string/number/element, otherwise the skeleton.
export function orSkel(value, skelWidth = 90) {
  if (value == null) return <Skel w={skelWidth} />;
  if (typeof value === "string" && (!value.trim() || value.trim() === "—")) return <Skel w={skelWidth} />;
  if (typeof value === "number" && !Number.isFinite(value)) return <Skel w={skelWidth} />;
  return value;
}
